import { Fragment, useEffect, useState } from "react";
import { PageLayout } from "@/ui/layouts";
import { Button, Card, Input, Loader, Modal } from "@/ui/atoms";
import { EmptyState, FormField } from "@/ui/molecules";
import type {
    TAsyncState,
    TCompanySftpSettings,
    TCompanyYardiMasterDataSettings,
    TFileProcessingThreshold,
    TSftpDeliveryInstance,
    TSftpEnvironmentDefaults,
    TSftpFileSubmission,
    TSftpSubmissionStatus,
    TYardiMasterDataSyncRun,
} from "@/store";
import { cn } from "@/utils/utils";
import {
    Activity,
    CalendarClock,
    ChevronDown,
    ChevronRight,
    Pencil,
    Plus,
    RefreshCw,
    Send,
    Server,
    Trash2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { formatTimeHHmm, validateThresholdDraft } from "./helpers";

type TabId = "sftp-connection" | "submission-schedule" | "sftp-activity" | "master-data-sync";

const INSTANCE_LABELS: Record<TSftpDeliveryInstance, string> = {
    replica: "Replica",
    live: "Live",
};

type TThresholdDraft = {
    startTime: string;
    endTime: string;
    threshold: string;
    instance: TSftpDeliveryInstance;
};

const createEmptyThresholdDraft = (): TThresholdDraft => ({
    startTime: "",
    endTime: "",
    threshold: "0",
    instance: "replica",
});

/**
 * Payload for saving SFTP connection settings. Secret fields are write-only:
 * undefined keeps the stored value, "" clears it.
 */
export type TSftpSettingsSaveData = {
    enabled: boolean;
    remote_directory: string;
    file_name_pattern: string;
    notes: string | null;
    host: string;
    port: number;
    username: string;
    known_hosts: string;
    password?: string;
    private_key?: string;
    private_key_passphrase?: string;
};

/**
 * Result of a live SFTP connection check. Failures carry the details shown
 * in the popup.
 */
export type TSftpConnectionTestResult = {
    success: boolean;
    remoteDirectory: string;
    entryCount: number | null;
    errorCode: string | null;
    errorMessage: string | null;
};

/**
 * Payload for saving Yardi master-data sync settings. The bearer token is
 * write-only: omitted keeps the stored value, "" clears it.
 */
export type TYardiMasterDataSettingsSaveData = {
    base_url: string;
    bearer_token?: string;
};

/**
 * View parameters (injected dependencies)
 */
export type TViewCompanySettingsParams = {
    useCases: () => {
        onMount: () => void;
        onUnmount: () => void;
        useThresholdsState: () => TAsyncState<TFileProcessingThreshold[]>;
        useSftpSettingsState: () => TAsyncState<TCompanySftpSettings | null>;
        /** Environment-level SFTP defaults (null until the settings load). */
        useSftpEnvDefaults: () => TSftpEnvironmentDefaults | null;
        useSftpSubmissionsState: () => TAsyncState<TSftpFileSubmission[]>;
        useMasterDataSettingsState: () => TAsyncState<TCompanyYardiMasterDataSettings | null>;
        useMasterDataSyncRunsState: () => TAsyncState<TYardiMasterDataSyncRun[]>;
        onRefresh: () => void;
        onCreateThreshold: (data: {
            start_time: string;
            end_time: string;
            threshold: number;
            instance: TSftpDeliveryInstance;
        }) => Promise<void>;
        onUpdateThreshold: (
            id: string,
            data: {
                start_time?: string;
                end_time?: string;
                threshold?: number;
                instance?: TSftpDeliveryInstance;
            },
        ) => Promise<void>;
        onDeleteThreshold: (id: string) => Promise<void>;
        onSaveSftpSettings: (data: TSftpSettingsSaveData) => Promise<void>;
        /** Live SFTP check against the saved connection settings (Proda only). */
        onTestSftpConnection: () => Promise<TSftpConnectionTestResult>;
        /** Download the exact payload of one upload attempt. */
        onDownloadTransferPayload: (transferId: string) => Promise<void>;
        onSaveMasterDataSettings: (data: TYardiMasterDataSettingsSaveData) => Promise<void>;
        /** Triggers a manual sync run, then the caller refreshes the run list. */
        onSyncMasterDataNow: () => Promise<void>;
        permissions: () => string[];
        /** Internal Proda admin — gates the SFTP Connection and Master Data Sync tabs */
        isProdaAdmin: () => boolean;
    };
};

/**
 * View props (from router/parent)
 */
export type TViewCompanySettingsProps = Record<string, never>;

// ==========================================
// Submission Schedule tab (processing thresholds)
// ==========================================

type ThresholdsSectionProps = {
    thresholdsState: TAsyncState<TFileProcessingThreshold[]>;
    canCreate: boolean;
    canEdit: boolean;
    canDelete: boolean;
    onRefresh: () => void;
    onCreate: (data: {
        start_time: string;
        end_time: string;
        threshold: number;
        instance: TSftpDeliveryInstance;
    }) => Promise<void>;
    onUpdate: (
        id: string,
        data: {
            start_time?: string;
            end_time?: string;
            threshold?: number;
            instance?: TSftpDeliveryInstance;
        },
    ) => Promise<void>;
    onDelete: (id: string) => Promise<void>;
};

const ThresholdsSection: React.FC<ThresholdsSectionProps> = ({
    thresholdsState,
    canCreate,
    canEdit,
    canDelete,
    onRefresh,
    onCreate,
    onUpdate,
    onDelete,
}) => {
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editDraft, setEditDraft] = useState<TThresholdDraft | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [newDraft, setNewDraft] = useState<TThresholdDraft>(createEmptyThresholdDraft);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const isLoading = thresholdsState.status === "pending" && !thresholdsState.data;
    const hasError = thresholdsState.status === "rejected";
    const thresholds = thresholdsState.data ?? [];

    const startEdit = (row: TFileProcessingThreshold): void => {
        setEditingId(row.id);
        setDeletingId(null);
        setIsAddingNew(false);
        setSaveError(null);
        setEditDraft({
            startTime: formatTimeHHmm(row.startTime),
            endTime: formatTimeHHmm(row.endTime),
            threshold: String(row.threshold),
            instance: row.instance,
        });
    };

    const cancelEdit = (): void => {
        setEditingId(null);
        setEditDraft(null);
        setSaveError(null);
    };

    const saveEdit = async (): Promise<void> => {
        if (!editingId || !editDraft) {
            return;
        }
        const validationError = validateThresholdDraft(editDraft);
        if (validationError) {
            setSaveError(validationError);
            return;
        }

        setIsSaving(true);
        setSaveError(null);
        try {
            await onUpdate(editingId, {
                start_time: editDraft.startTime,
                end_time: editDraft.endTime,
                threshold: parseInt(editDraft.threshold, 10),
                instance: editDraft.instance,
            });
            cancelEdit();
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : "Failed to update threshold");
        } finally {
            setIsSaving(false);
        }
    };

    const saveNew = async (): Promise<void> => {
        const validationError = validateThresholdDraft(newDraft);
        if (validationError) {
            setSaveError(validationError);
            return;
        }

        setIsSaving(true);
        setSaveError(null);
        try {
            await onCreate({
                start_time: newDraft.startTime,
                end_time: newDraft.endTime,
                threshold: parseInt(newDraft.threshold, 10),
                instance: newDraft.instance,
            });
            setIsAddingNew(false);
            setNewDraft(createEmptyThresholdDraft());
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : "Failed to create threshold");
        } finally {
            setIsSaving(false);
        }
    };

    const cancelNew = (): void => {
        setIsAddingNew(false);
        setNewDraft(createEmptyThresholdDraft());
        setSaveError(null);
    };

    const confirmDelete = async (id: string): Promise<void> => {
        setIsSaving(true);
        setSaveError(null);
        try {
            await onDelete(id);
            setDeletingId(null);
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : "Failed to delete threshold");
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center py-12">
                <Loader size="lg" />
            </div>
        );
    }

    if (hasError) {
        return (
            <EmptyState
                variant="error"
                title="Failed to load thresholds"
                description="An error occurred while loading processing threshold data"
                action={{ label: "Try Again", onClick: onRefresh }}
            />
        );
    }

    return (
        <div className="space-y-4">
            {canCreate && (
                <div className="flex justify-end">
                    <Button
                        onClick={() => {
                            setIsAddingNew(true);
                            setEditingId(null);
                            setEditDraft(null);
                            setDeletingId(null);
                            setSaveError(null);
                        }}
                        disabled={isAddingNew || isSaving}
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        Add threshold
                    </Button>
                </div>
            )}

            {saveError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {saveError}
                </div>
            )}

            <Card className="overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                        <tr>
                            <th className="text-left font-semibold px-4 py-3">Start Time (UTC)</th>
                            <th className="text-left font-semibold px-4 py-3">End Time (UTC)</th>
                            <th className="text-left font-semibold px-4 py-3">Threshold</th>
                            <th className="text-left font-semibold px-4 py-3">Instance</th>
                            <th className="text-left font-semibold px-4 py-3">Created</th>
                            <th className="text-left font-semibold px-4 py-3">Updated</th>
                            <th className="text-right font-semibold px-4 py-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {thresholds.map((row) => {
                            const isEditing = editingId === row.id;
                            return (
                                <tr
                                    key={row.id}
                                    className="border-b last:border-b-0 even:bg-muted/40"
                                >
                                    <td className="px-4 py-3">
                                        {isEditing && editDraft ? (
                                            <Input
                                                type="time"
                                                value={editDraft.startTime}
                                                onChange={(event) =>
                                                    setEditDraft({
                                                        ...editDraft,
                                                        startTime: event.target.value,
                                                    })
                                                }
                                            />
                                        ) : (
                                            <span className="font-mono font-medium">
                                                {formatTimeHHmm(row.startTime)}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        {isEditing && editDraft ? (
                                            <Input
                                                type="time"
                                                value={editDraft.endTime}
                                                onChange={(event) =>
                                                    setEditDraft({
                                                        ...editDraft,
                                                        endTime: event.target.value,
                                                    })
                                                }
                                            />
                                        ) : (
                                            <span className="font-mono font-medium">
                                                {formatTimeHHmm(row.endTime)}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        {isEditing && editDraft ? (
                                            <Input
                                                type="number"
                                                min={0}
                                                value={editDraft.threshold}
                                                onChange={(event) =>
                                                    setEditDraft({
                                                        ...editDraft,
                                                        threshold: event.target.value,
                                                    })
                                                }
                                            />
                                        ) : (
                                            <span>{row.threshold}</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        {isEditing && editDraft ? (
                                            <select
                                                aria-label="Instance"
                                                value={editDraft.instance}
                                                onChange={(event) =>
                                                    setEditDraft({
                                                        ...editDraft,
                                                        instance: event.target
                                                            .value as TSftpDeliveryInstance,
                                                    })
                                                }
                                                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                            >
                                                <option value="replica">Replica</option>
                                                <option value="live">Live</option>
                                            </select>
                                        ) : (
                                            <span>{INSTANCE_LABELS[row.instance]}</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-muted-foreground">
                                        {new Date(row.createdAt).toLocaleString()}
                                    </td>
                                    <td className="px-4 py-3 text-muted-foreground">
                                        {new Date(row.updatedAt).toLocaleString()}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        {isEditing ? (
                                            <div className="inline-flex gap-2">
                                                <Button
                                                    size="sm"
                                                    onClick={() => {
                                                        void saveEdit();
                                                    }}
                                                    disabled={isSaving}
                                                >
                                                    Save
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={cancelEdit}
                                                    disabled={isSaving}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        ) : deletingId === row.id ? (
                                            <div className="inline-flex gap-2">
                                                <Button
                                                    size="sm"
                                                    variant="destructive"
                                                    onClick={() => {
                                                        void confirmDelete(row.id);
                                                    }}
                                                    disabled={isSaving}
                                                >
                                                    Confirm
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => setDeletingId(null)}
                                                    disabled={isSaving}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="inline-flex gap-2">
                                                {canEdit && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => startEdit(row)}
                                                        disabled={isSaving}
                                                    >
                                                        <Pencil className="h-3 w-3 mr-1" />
                                                        Edit
                                                    </Button>
                                                )}
                                                {canDelete && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => {
                                                            setDeletingId(row.id);
                                                            setSaveError(null);
                                                        }}
                                                        disabled={isSaving}
                                                    >
                                                        <Trash2 className="h-3 w-3 mr-1" />
                                                        Delete
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}

                        {isAddingNew && (
                            <tr className="border-b last:border-b-0 bg-slate-50/60">
                                <td className="px-4 py-3">
                                    <Input
                                        type="time"
                                        value={newDraft.startTime}
                                        onChange={(event) =>
                                            setNewDraft({
                                                ...newDraft,
                                                startTime: event.target.value,
                                            })
                                        }
                                    />
                                </td>
                                <td className="px-4 py-3">
                                    <Input
                                        type="time"
                                        value={newDraft.endTime}
                                        onChange={(event) =>
                                            setNewDraft({
                                                ...newDraft,
                                                endTime: event.target.value,
                                            })
                                        }
                                    />
                                </td>
                                <td className="px-4 py-3">
                                    <Input
                                        type="number"
                                        min={0}
                                        placeholder="Threshold"
                                        value={newDraft.threshold}
                                        onChange={(event) =>
                                            setNewDraft({
                                                ...newDraft,
                                                threshold: event.target.value,
                                            })
                                        }
                                    />
                                </td>
                                <td className="px-4 py-3">
                                    <select
                                        aria-label="Instance"
                                        value={newDraft.instance}
                                        onChange={(event) =>
                                            setNewDraft({
                                                ...newDraft,
                                                instance: event.target
                                                    .value as TSftpDeliveryInstance,
                                            })
                                        }
                                        className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                    >
                                        <option value="replica">Replica</option>
                                        <option value="live">Live</option>
                                    </select>
                                </td>
                                <td className="px-4 py-3" />
                                <td className="px-4 py-3" />
                                <td className="px-4 py-3 text-right">
                                    <div className="inline-flex gap-2">
                                        <Button
                                            size="sm"
                                            onClick={() => {
                                                void saveNew();
                                            }}
                                            disabled={isSaving}
                                        >
                                            Save
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={cancelNew}
                                            disabled={isSaving}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        )}

                        {thresholds.length === 0 && !isAddingNew && (
                            <tr>
                                <td
                                    colSpan={7}
                                    className="px-4 py-8 text-center text-sm text-muted-foreground"
                                >
                                    No processing thresholds configured yet.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </Card>
        </div>
    );
};

// ==========================================
// SFTP Connection tab (Proda admins only)
// ==========================================

// Tokens resolved by the backend name builder (core/services/sftp_delivery).
const fileNamePatternInfo = (
    <div className="space-y-1.5">
        <p className="font-semibold">Available template tokens</p>
        <ul className="space-y-1">
            <li>
                <code className="font-mono">{"{platform}"}</code> — &quot;Platform&quot; (Yardi) or
                &quot;DataPlatform&quot; (Snowflake)
            </li>
            <li>
                <code className="font-mono">{"{instance}"}</code> — &quot;Replica&quot;; omitted
                (with its separator) for live uploads
            </li>
            <li>
                <code className="font-mono">{"{timing}"}</code> — &quot;prompt&quot; when uploaded
                right away, &quot;delayed&quot; when it waited for a window
            </li>
            <li>
                <code className="font-mono">{"{unique_id}"}</code> — unique upload key (required);
                Revantage reports back by this key
            </li>
        </ul>
        <p>
            Leave blank for the default:{" "}
            <code className="font-mono">{"{platform}_{instance}_{timing}-{unique_id}"}</code>
        </p>
    </div>
);

type TSftpSettingsSectionProps = {
    sftpSettingsState: TAsyncState<TCompanySftpSettings | null>;
    envDefaults: TSftpEnvironmentDefaults | null;
    onRefresh: () => void;
    onSave: (data: TSftpSettingsSaveData) => Promise<void>;
    onTestConnection: () => Promise<TSftpConnectionTestResult>;
};

const secretPlaceholder = (isSet: boolean, envHas: boolean): string =>
    isSet
        ? "Set — leave blank to keep, enter a new value to replace"
        : envHas
          ? "Not set here — environment value in use"
          : "Not set";

/** "Clear stored value" checkbox shown when a company secret is stored. */
const SecretClearToggle: React.FC<{
    visible: boolean;
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled: boolean;
    testId: string;
}> = ({ visible, checked, onChange, disabled, testId }) => {
    if (!visible) {
        return null;
    }
    return (
        <label className="mt-1 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <input
                type="checkbox"
                data-testid={testId}
                checked={checked}
                onChange={(event) => onChange(event.target.checked)}
                disabled={disabled}
            />
            Clear stored value on save (fall back to environment)
        </label>
    );
};

/** SHA-256 fingerprint line for a write-only secret (stored or environment). */
const SecretFingerprint: React.FC<{
    stored: string | null | undefined;
    env: string | null | undefined;
}> = ({ stored, env }) => {
    const hash = stored ?? env;
    if (!hash) {
        return null;
    }
    return (
        <p
            className="mt-1 font-mono text-[11px] leading-tight text-muted-foreground break-all"
            title="Verify with: printf %s '<secret>' | sha256sum"
        >
            {stored ? "Stored" : "Environment"} SHA-256: {hash}
        </p>
    );
};

const SftpSettingsSection: React.FC<TSftpSettingsSectionProps> = ({
    sftpSettingsState,
    envDefaults,
    onRefresh,
    onSave,
    onTestConnection,
}) => {
    const [enabled, setEnabled] = useState(false);
    const [remoteDirectory, setRemoteDirectory] = useState("");
    const [fileNamePattern, setFileNamePattern] = useState("");
    const [notes, setNotes] = useState("");
    const [host, setHost] = useState("");
    const [port, setPort] = useState("22");
    const [username, setUsername] = useState("");
    const [knownHosts, setKnownHosts] = useState("");
    // Write-only secrets: empty means "keep the stored value"
    const [password, setPassword] = useState("");
    const [privateKey, setPrivateKey] = useState("");
    const [privateKeyPassphrase, setPrivateKeyPassphrase] = useState("");
    // Explicit clear flags — checked sends "" (clear and fall back to env)
    const [clearPassword, setClearPassword] = useState(false);
    const [clearPrivateKey, setClearPrivateKey] = useState(false);
    const [clearPrivateKeyPassphrase, setClearPrivateKeyPassphrase] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [isTesting, setIsTesting] = useState(false);
    const [testResult, setTestResult] = useState<TSftpConnectionTestResult | null>(null);
    const [isTestDetailsOpen, setIsTestDetailsOpen] = useState(false);

    const settings = sftpSettingsState.data ?? null;

    const handleTestConnection = async (): Promise<void> => {
        setIsTesting(true);
        setTestResult(null);
        try {
            setTestResult(await onTestConnection());
        } catch (err) {
            setTestResult({
                success: false,
                remoteDirectory: "",
                entryCount: null,
                errorCode: null,
                errorMessage:
                    err instanceof Error ? err.message : "Failed to run the connection test",
            });
        } finally {
            setIsTesting(false);
        }
    };

    // Sync the form when settings load (null means not yet configured — keep
    // defaults). Secret inputs stay blank — the API never returns the values.
    useEffect(() => {
        if (settings) {
            setEnabled(settings.enabled);
            setRemoteDirectory(settings.remoteDirectory);
            setFileNamePattern(settings.fileNamePattern);
            setNotes(settings.notes ?? "");
            setHost(settings.host);
            setPort(String(settings.port));
            setUsername(settings.username);
            setKnownHosts(settings.knownHosts);
            setPassword("");
            setPrivateKey("");
            setPrivateKeyPassphrase("");
            setClearPassword(false);
            setClearPrivateKey(false);
            setClearPrivateKeyPassphrase(false);
        }
    }, [settings]);

    const isLoading = sftpSettingsState.status === "pending" || sftpSettingsState.status === "idle";
    const hasError = sftpSettingsState.status === "rejected";

    const handleSave = async (): Promise<void> => {
        const portNumber = Number.parseInt(port, 10);
        if (Number.isNaN(portNumber) || portNumber < 1 || portNumber > 65535) {
            setSaveError("Port must be a number between 1 and 65535");
            setSaveSuccess(false);
            return;
        }

        setIsSaving(true);
        setSaveError(null);
        setSaveSuccess(false);
        try {
            await onSave({
                enabled,
                remote_directory: remoteDirectory.trim(),
                file_name_pattern: fileNamePattern.trim(),
                notes: notes.trim() ? notes.trim() : null,
                host: host.trim(),
                port: portNumber,
                username: username.trim(),
                known_hosts: knownHosts.trim(),
                // Blank secret inputs mean "keep the stored value"; an
                // explicit clear sends "" (falls back to the environment).
                ...(clearPassword ? { password: "" } : password ? { password } : {}),
                ...(clearPrivateKey
                    ? { private_key: "" }
                    : privateKey
                      ? { private_key: privateKey }
                      : {}),
                ...(clearPrivateKeyPassphrase
                    ? { private_key_passphrase: "" }
                    : privateKeyPassphrase
                      ? { private_key_passphrase: privateKeyPassphrase }
                      : {}),
            });
            setSaveSuccess(true);
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : "Failed to save SFTP settings");
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center py-12">
                <Loader size="lg" />
            </div>
        );
    }

    if (hasError) {
        return (
            <EmptyState
                variant="error"
                title="Failed to load SFTP settings"
                description="An error occurred while loading SFTP connection settings"
                action={{ label: "Try Again", onClick: onRefresh }}
            />
        );
    }

    return (
        <Card className="max-w-2xl p-6 space-y-4">
            {saveError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {saveError}
                </div>
            )}
            {saveSuccess && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                    SFTP settings saved.
                </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
                <label className="inline-flex items-center gap-2">
                    <input
                        type="checkbox"
                        checked={enabled}
                        onChange={(event) => setEnabled(event.target.checked)}
                        disabled={isSaving}
                    />
                    <span className="text-sm font-medium">Enabled</span>
                </label>
                {enabled && (
                    <Button
                        data-testid="test-sftp-connection-button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                            void handleTestConnection();
                        }}
                        disabled={isTesting}
                        title="Lists the remote directory using the saved connection settings"
                    >
                        {isTesting ? "Testing…" : "Test connection"}
                    </Button>
                )}
                {enabled && testResult && (
                    <span
                        data-testid="test-sftp-connection-result"
                        className="inline-flex items-center gap-1.5 text-sm"
                    >
                        <span
                            className={cn(
                                "h-2.5 w-2.5 rounded-full",
                                testResult.success ? "bg-emerald-500" : "bg-red-500",
                            )}
                        />
                        {testResult.success ? (
                            <span className="text-emerald-700">
                                Connection OK
                                {testResult.entryCount !== null &&
                                    ` — ${testResult.entryCount} entries`}
                            </span>
                        ) : (
                            <>
                                <span className="text-red-700">Connection failed</span>
                                <button
                                    type="button"
                                    data-testid="test-sftp-connection-details"
                                    onClick={() => setIsTestDetailsOpen(true)}
                                    className="text-primary underline underline-offset-2 hover:opacity-80"
                                >
                                    View details
                                </button>
                            </>
                        )}
                    </span>
                )}
            </div>

            <Modal
                open={isTestDetailsOpen}
                onOpenChange={setIsTestDetailsOpen}
                title="SFTP connection test failed"
                description="Details of the last directory listing attempt"
            >
                <div className="space-y-3 text-sm">
                    {testResult?.remoteDirectory && (
                        <div>
                            <span className="font-medium">Remote directory: </span>
                            <code className="font-mono">{testResult.remoteDirectory}</code>
                        </div>
                    )}
                    {testResult?.errorCode && (
                        <div>
                            <span className="font-medium">Error code: </span>
                            <code className="font-mono">{testResult.errorCode}</code>
                        </div>
                    )}
                    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 whitespace-pre-wrap break-words">
                        {testResult?.errorMessage ?? "No error details available."}
                    </div>
                </div>
            </Modal>

            <div className="grid gap-4 sm:grid-cols-[1fr_8rem]">
                <FormField label="Host" htmlFor="sftp-host">
                    <Input
                        id="sftp-host"
                        value={host}
                        onChange={(event) => setHost(event.target.value)}
                        placeholder={
                            envDefaults?.host
                                ? `${envDefaults.host} (environment)`
                                : "SFTP host name or IP"
                        }
                        disabled={isSaving}
                    />
                </FormField>
                <FormField label="Port" htmlFor="sftp-port">
                    <Input
                        id="sftp-port"
                        type="number"
                        min={1}
                        max={65535}
                        value={port}
                        onChange={(event) => setPort(event.target.value)}
                        disabled={isSaving}
                    />
                </FormField>
            </div>

            <FormField label="Username" htmlFor="sftp-username">
                <Input
                    id="sftp-username"
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                    placeholder={
                        envDefaults?.username ? `${envDefaults.username} (environment)` : undefined
                    }
                    autoComplete="off"
                    disabled={isSaving}
                />
            </FormField>

            <FormField label="Password" htmlFor="sftp-password">
                <Input
                    id="sftp-password"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder={secretPlaceholder(
                        settings?.hasPassword ?? false,
                        envDefaults?.hasPassword ?? false,
                    )}
                    autoComplete="new-password"
                    disabled={isSaving || clearPassword}
                />
                <SecretFingerprint
                    stored={settings?.passwordSha256}
                    env={envDefaults?.passwordSha256}
                />
                <SecretClearToggle
                    visible={settings?.hasPassword ?? false}
                    checked={clearPassword}
                    onChange={setClearPassword}
                    disabled={isSaving}
                    testId="clear-sftp-password"
                />
            </FormField>

            <FormField label="SSH private key" htmlFor="sftp-private-key">
                <textarea
                    id="sftp-private-key"
                    value={privateKey}
                    onChange={(event) => setPrivateKey(event.target.value)}
                    className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none font-mono text-xs"
                    placeholder={secretPlaceholder(
                        settings?.hasPrivateKey ?? false,
                        envDefaults?.hasPrivateKey ?? false,
                    )}
                    rows={4}
                    spellCheck={false}
                    disabled={isSaving || clearPrivateKey}
                />
                <SecretFingerprint
                    stored={settings?.privateKeySha256}
                    env={envDefaults?.privateKeySha256}
                />
                <SecretClearToggle
                    visible={settings?.hasPrivateKey ?? false}
                    checked={clearPrivateKey}
                    onChange={setClearPrivateKey}
                    disabled={isSaving}
                    testId="clear-sftp-private-key"
                />
            </FormField>

            <FormField label="Private key passphrase" htmlFor="sftp-private-key-passphrase">
                <Input
                    id="sftp-private-key-passphrase"
                    type="password"
                    value={privateKeyPassphrase}
                    onChange={(event) => setPrivateKeyPassphrase(event.target.value)}
                    placeholder={secretPlaceholder(
                        settings?.hasPrivateKeyPassphrase ?? false,
                        envDefaults?.hasPrivateKeyPassphrase ?? false,
                    )}
                    autoComplete="new-password"
                    disabled={isSaving || clearPrivateKeyPassphrase}
                />
                <SecretFingerprint
                    stored={settings?.privateKeyPassphraseSha256}
                    env={envDefaults?.privateKeyPassphraseSha256}
                />
                <SecretClearToggle
                    visible={settings?.hasPrivateKeyPassphrase ?? false}
                    checked={clearPrivateKeyPassphrase}
                    onChange={setClearPrivateKeyPassphrase}
                    disabled={isSaving}
                    testId="clear-sftp-private-key-passphrase"
                />
            </FormField>

            <FormField label="Known hosts" htmlFor="sftp-known-hosts">
                <textarea
                    id="sftp-known-hosts"
                    value={knownHosts}
                    onChange={(event) => setKnownHosts(event.target.value)}
                    className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none font-mono text-xs"
                    placeholder={
                        envDefaults?.hasKnownHosts
                            ? "Blank — environment known_hosts in use"
                            : "OpenSSH known_hosts content"
                    }
                    rows={3}
                    spellCheck={false}
                    disabled={isSaving}
                />
            </FormField>

            <FormField label="Remote directory" htmlFor="sftp-remote-directory">
                <Input
                    id="sftp-remote-directory"
                    value={remoteDirectory}
                    onChange={(event) => setRemoteDirectory(event.target.value)}
                    placeholder="/inbound/finance"
                    disabled={isSaving}
                />
            </FormField>

            <FormField
                label="File name pattern"
                htmlFor="sftp-file-name-pattern"
                info={fileNamePatternInfo}
            >
                <Input
                    id="sftp-file-name-pattern"
                    value={fileNamePattern}
                    onChange={(event) => setFileNamePattern(event.target.value)}
                    placeholder="{platform}_{instance}_{timing}-{unique_id}"
                    disabled={isSaving}
                />
            </FormField>

            <FormField label="Notes" htmlFor="sftp-notes">
                <textarea
                    id="sftp-notes"
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                    rows={3}
                    disabled={isSaving}
                />
            </FormField>

            <div className="flex justify-end">
                <Button
                    onClick={() => {
                        void handleSave();
                    }}
                    disabled={isSaving}
                >
                    Save
                </Button>
            </div>
        </Card>
    );
};

// ==========================================
// Master Data Sync tab (Proda admins only)
// ==========================================

type TYardiMasterDataSyncRunStatus = TYardiMasterDataSyncRun["status"];

const SYNC_RUN_STATUS_LABELS: Record<TYardiMasterDataSyncRunStatus, string> = {
    running: "Running",
    succeeded: "Succeeded",
    failed: "Failed",
};

const SYNC_RUN_STATUS_STYLES: Record<TYardiMasterDataSyncRunStatus, string> = {
    running: "bg-blue-100 text-blue-800",
    succeeded: "bg-emerald-100 text-emerald-800",
    failed: "bg-red-100 text-red-700",
};

const SyncRunStatusBadge: React.FC<{ status: TYardiMasterDataSyncRunStatus }> = ({ status }) => (
    <span
        className={cn(
            "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
            SYNC_RUN_STATUS_STYLES[status],
        )}
    >
        {SYNC_RUN_STATUS_LABELS[status]}
    </span>
);

// The daily schedule means most runs are automatic, so the history has to say which
// is which — otherwise "Sync now" and the 07:00 run are indistinguishable here.
const SYNC_RUN_TRIGGER_LABELS: Record<TYardiMasterDataSyncRun["trigger"], string> = {
    manual: "Manual",
    scheduled: "Scheduled",
};

/** "14 objects, 1,234 records" summary, or null when there's nothing to summarize. */
const formatObjectCounts = (counts: Record<string, number> | null): string | null => {
    if (!counts) {
        return null;
    }
    const objectNames = Object.keys(counts);
    if (objectNames.length === 0) {
        return null;
    }
    const recordCount = Object.values(counts).reduce((sum, count) => sum + count, 0);
    return `${objectNames.length} object${objectNames.length === 1 ? "" : "s"}, ${recordCount.toLocaleString()} record${recordCount === 1 ? "" : "s"}`;
};

type MasterDataSyncSectionProps = {
    settingsState: TAsyncState<TCompanyYardiMasterDataSettings | null>;
    syncRunsState: TAsyncState<TYardiMasterDataSyncRun[]>;
    onRefresh: () => void;
    onSave: (data: TYardiMasterDataSettingsSaveData) => Promise<void>;
    onSyncNow: () => Promise<void>;
};

const MasterDataSyncSection: React.FC<MasterDataSyncSectionProps> = ({
    settingsState,
    syncRunsState,
    onRefresh,
    onSave,
    onSyncNow,
}) => {
    const [baseUrl, setBaseUrl] = useState("");
    // Write-only secret: empty means "keep the stored value"
    const [bearerToken, setBearerToken] = useState("");
    // Explicit clear flag — checked sends "" (clears the stored token)
    const [clearToken, setClearToken] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncError, setSyncError] = useState<string | null>(null);

    const settings = settingsState.data ?? null;

    // Sync the form when settings load (null means not yet configured — keep
    // defaults). The token input stays blank — the API never returns it.
    useEffect(() => {
        if (settings) {
            setBaseUrl(settings.baseUrl);
            setBearerToken("");
            setClearToken(false);
        }
    }, [settings]);

    const isLoading = settingsState.status === "pending" || settingsState.status === "idle";
    const hasError = settingsState.status === "rejected";

    const runs = syncRunsState.data ?? [];
    const latestRun = runs[0] ?? null;
    const isConfigured = settings !== null && settings.baseUrl.trim().length > 0;
    const syncDisabled = !isConfigured || latestRun?.status === "running" || isSyncing;

    const handleSave = async (): Promise<void> => {
        setIsSaving(true);
        setSaveError(null);
        setSaveSuccess(false);
        try {
            await onSave({
                base_url: baseUrl.trim(),
                // Blank keeps the stored token; an explicit clear sends "".
                ...(clearToken
                    ? { bearer_token: "" }
                    : bearerToken
                      ? { bearer_token: bearerToken }
                      : {}),
            });
            setSaveSuccess(true);
        } catch (err) {
            setSaveError(
                err instanceof Error ? err.message : "Failed to save master data sync settings",
            );
        } finally {
            setIsSaving(false);
        }
    };

    const handleSyncNow = async (): Promise<void> => {
        setIsSyncing(true);
        setSyncError(null);
        try {
            await onSyncNow();
        } catch (err) {
            setSyncError(err instanceof Error ? err.message : "Failed to trigger sync");
        } finally {
            setIsSyncing(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center py-12">
                <Loader size="lg" />
            </div>
        );
    }

    if (hasError) {
        return (
            <EmptyState
                variant="error"
                title="Failed to load master data sync settings"
                description="An error occurred while loading Yardi master data sync settings"
                action={{ label: "Try Again", onClick: onRefresh }}
            />
        );
    }

    return (
        <Card data-testid="master-data-sync-section" className="max-w-2xl p-6 space-y-4">
            {saveError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {saveError}
                </div>
            )}
            {saveSuccess && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                    Master data sync settings saved.
                </div>
            )}

            <FormField label="Base URL" htmlFor="master-data-sync-base-url">
                <Input
                    id="master-data-sync-base-url"
                    data-testid="master-data-sync-base-url-input"
                    value={baseUrl}
                    onChange={(event) => setBaseUrl(event.target.value)}
                    placeholder="https://emea.snaplogic.com/api/1/rest/slsched/feed/<env>/<pipeline>"
                    disabled={isSaving}
                />
            </FormField>

            <FormField label="Bearer token" htmlFor="master-data-sync-token">
                <Input
                    id="master-data-sync-token"
                    data-testid="master-data-sync-token-input"
                    type="password"
                    value={bearerToken}
                    onChange={(event) => setBearerToken(event.target.value)}
                    placeholder={secretPlaceholder(settings?.hasBearerToken ?? false, false)}
                    autoComplete="new-password"
                    disabled={isSaving || clearToken}
                />
                <SecretFingerprint stored={settings?.bearerTokenSha256} env={null} />
                <SecretClearToggle
                    visible={settings?.hasBearerToken ?? false}
                    checked={clearToken}
                    onChange={setClearToken}
                    disabled={isSaving}
                    testId="clear-master-data-sync-token"
                />
            </FormField>

            <div className="flex justify-end">
                <Button
                    data-testid="master-data-sync-save-button"
                    onClick={() => {
                        void handleSave();
                    }}
                    disabled={isSaving}
                >
                    Save
                </Button>
            </div>

            <div className="border-t pt-4 flex items-center justify-between gap-4">
                <div>
                    <p className="text-sm font-medium">Sync now</p>
                    <p className="text-xs text-muted-foreground">
                        Runs a manual master-data sync against the settings above.
                    </p>
                </div>
                <Button
                    data-testid="master-data-sync-sync-button"
                    variant="outline"
                    onClick={() => {
                        void handleSyncNow();
                    }}
                    disabled={syncDisabled}
                >
                    {isSyncing ? "Starting…" : "Sync now"}
                </Button>
            </div>
            {syncError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {syncError}
                </div>
            )}

            <div className="border-t pt-4 space-y-2">
                <p className="text-sm font-medium">Run history</p>
                {settings === null ? (
                    <p className="text-sm text-muted-foreground">Not configured yet.</p>
                ) : runs.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No sync runs yet.</p>
                ) : (
                    <ul className="space-y-2">
                        {runs.map((run) => {
                            const objectSummary = formatObjectCounts(run.objectCounts);
                            return (
                                <li
                                    key={run.id}
                                    className="rounded-lg border px-3 py-2 text-sm space-y-1"
                                >
                                    <div className="flex flex-wrap items-center gap-2">
                                        <SyncRunStatusBadge status={run.status} />
                                        <span className="text-muted-foreground">
                                            {SYNC_RUN_TRIGGER_LABELS[run.trigger] ?? run.trigger}
                                        </span>
                                        <span className="text-muted-foreground">
                                            Started {new Date(run.startedAt).toLocaleString()}
                                        </span>
                                        <span className="text-muted-foreground">
                                            {run.finishedAt
                                                ? `· Finished ${new Date(run.finishedAt).toLocaleString()}`
                                                : "· In progress"}
                                        </span>
                                    </div>
                                    {objectSummary && (
                                        <p className="text-xs text-muted-foreground">
                                            {objectSummary}
                                        </p>
                                    )}
                                    {run.error && (
                                        <p className="text-xs text-red-700">{run.error}</p>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </Card>
    );
};

// ==========================================
// SFTP Activity tab (submission tracking)
// ==========================================

const SUBMISSION_STATUS_LABELS: Record<TSftpSubmissionStatus, string> = {
    queued: "Queued",
    waiting_for_window: "Waiting for upload window",
    uploading: "Uploading",
    uploaded: "Uploaded to SFTP",
    failed: "Failed",
    processed: "Processed by Yardi",
    rejected: "Rejected by Yardi",
};

const SUBMISSION_STATUS_STYLES: Record<TSftpSubmissionStatus, string> = {
    queued: "bg-slate-100 text-slate-700",
    waiting_for_window: "bg-amber-100 text-amber-800",
    uploading: "bg-blue-100 text-blue-800",
    uploaded: "bg-emerald-100 text-emerald-800",
    failed: "bg-red-100 text-red-700",
    processed: "bg-emerald-100 text-emerald-800",
    rejected: "bg-red-100 text-red-700",
};

const StatusBadge: React.FC<{ status: TSftpSubmissionStatus }> = ({ status }) => (
    <span
        className={cn(
            "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
            SUBMISSION_STATUS_STYLES[status],
        )}
    >
        {SUBMISSION_STATUS_LABELS[status]}
    </span>
);

const formatBytes = (bytes: number | null): string => {
    if (bytes === null) {
        return "—";
    }
    if (bytes < 1024) {
        return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

type TSftpActivitySectionProps = {
    submissionsState: TAsyncState<TSftpFileSubmission[]>;
    onRefresh: () => void;
    onDownloadTransfer: (transferId: string) => Promise<void>;
};

const SftpActivitySection: React.FC<TSftpActivitySectionProps> = ({
    submissionsState,
    onRefresh,
    onDownloadTransfer,
}) => {
    const [expandedId, setExpandedId] = useState<string | null>(null);

    const isLoading = submissionsState.status === "pending" && !submissionsState.data;
    const hasError = submissionsState.status === "rejected";
    const submissions = submissionsState.data ?? [];

    if (isLoading) {
        return (
            <div className="flex justify-center py-12">
                <Loader size="lg" />
            </div>
        );
    }

    if (hasError) {
        return (
            <EmptyState
                variant="error"
                title="Failed to load SFTP activity"
                description="An error occurred while loading file submission activity"
                action={{ label: "Try Again", onClick: onRefresh }}
            />
        );
    }

    return (
        <Card className="overflow-hidden">
            <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                    <tr>
                        <th className="w-8 px-2 py-3" />
                        <th className="text-left font-semibold px-4 py-3">File</th>
                        <th className="text-left font-semibold px-4 py-3">Period</th>
                        <th className="text-left font-semibold px-4 py-3">Line Items</th>
                        <th className="text-left font-semibold px-4 py-3">Instance</th>
                        <th className="text-left font-semibold px-4 py-3">Stage</th>
                        <th className="text-left font-semibold px-4 py-3">Triggered By</th>
                        <th className="text-left font-semibold px-4 py-3">Triggered</th>
                        <th className="text-left font-semibold px-4 py-3">Last Update</th>
                    </tr>
                </thead>
                <tbody>
                    {submissions.map((submission) => {
                        const isExpanded = expandedId === submission.id;
                        return (
                            <Fragment key={submission.id}>
                                <tr
                                    className="border-b last:border-b-0 even:bg-muted/40 cursor-pointer"
                                    onClick={() => setExpandedId(isExpanded ? null : submission.id)}
                                    data-testid={`sftp-activity-row-${submission.id}`}
                                >
                                    <td className="px-2 py-3 text-muted-foreground">
                                        {isExpanded ? (
                                            <ChevronDown className="h-4 w-4" />
                                        ) : (
                                            <ChevronRight className="h-4 w-4" />
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <Link
                                            to={`/files/${submission.financialFileId}`}
                                            className="font-medium text-primary hover:underline"
                                            onClick={(event) => event.stopPropagation()}
                                        >
                                            {submission.fileName ?? submission.uniqueKey}
                                        </Link>
                                    </td>
                                    <td className="px-4 py-3">{submission.periodLabel ?? "—"}</td>
                                    <td className="px-4 py-3">
                                        {submission.totalLineItems ?? "—"}
                                    </td>
                                    <td className="px-4 py-3">
                                        {INSTANCE_LABELS[submission.instance]}
                                    </td>
                                    <td className="px-4 py-3">
                                        <StatusBadge status={submission.status} />
                                    </td>
                                    <td className="px-4 py-3">
                                        {submission.triggeredBy ?? "System"}
                                    </td>
                                    <td className="px-4 py-3 text-muted-foreground">
                                        {new Date(submission.triggeredAt).toLocaleString()}
                                    </td>
                                    <td className="px-4 py-3 text-muted-foreground">
                                        {new Date(submission.updatedAt).toLocaleString()}
                                    </td>
                                </tr>
                                {isExpanded && (
                                    <tr className="border-b last:border-b-0 bg-slate-50/60">
                                        <td colSpan={9} className="px-6 py-4">
                                            <div className="grid gap-6 md:grid-cols-2">
                                                <div>
                                                    <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                                                        Stage History
                                                    </h4>
                                                    <ol className="space-y-2">
                                                        {submission.events.map((event, index) => (
                                                            <li
                                                                key={`${submission.id}-event-${index}`}
                                                                className="flex items-start gap-3"
                                                            >
                                                                <span className="text-xs text-muted-foreground whitespace-nowrap pt-0.5">
                                                                    {new Date(
                                                                        event.occurredAt,
                                                                    ).toLocaleString()}
                                                                </span>
                                                                <StatusBadge
                                                                    status={event.status}
                                                                />
                                                                {event.detail && (
                                                                    <span className="text-xs text-muted-foreground pt-0.5">
                                                                        {event.detail}
                                                                    </span>
                                                                )}
                                                            </li>
                                                        ))}
                                                    </ol>
                                                </div>
                                                <div>
                                                    <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                                                        Upload Attempts
                                                    </h4>
                                                    {submission.transfers.length === 0 ? (
                                                        <p className="text-xs text-muted-foreground">
                                                            No upload attempts yet.
                                                        </p>
                                                    ) : (
                                                        <ul className="space-y-2">
                                                            {submission.transfers.map(
                                                                (transfer, index) => (
                                                                    <li
                                                                        key={`${submission.id}-transfer-${index}`}
                                                                        className="text-xs space-y-0.5"
                                                                    >
                                                                        <div className="flex items-center gap-2">
                                                                            <span
                                                                                className={cn(
                                                                                    "font-medium",
                                                                                    transfer.status ===
                                                                                        "success"
                                                                                        ? "text-emerald-700"
                                                                                        : transfer.status ===
                                                                                            "failed"
                                                                                          ? "text-red-700"
                                                                                          : "text-blue-700",
                                                                                )}
                                                                            >
                                                                                {transfer.status}
                                                                            </span>
                                                                            <span className="text-muted-foreground">
                                                                                {new Date(
                                                                                    transfer.startedAt,
                                                                                ).toLocaleString()}
                                                                            </span>
                                                                            <span className="text-muted-foreground">
                                                                                {formatBytes(
                                                                                    transfer.sizeBytes,
                                                                                )}
                                                                                {transfer.durationMs !==
                                                                                    null &&
                                                                                    ` · ${transfer.durationMs} ms`}
                                                                            </span>
                                                                        </div>
                                                                        <div className="font-mono text-muted-foreground break-all">
                                                                            {transfer.remotePath}
                                                                            {transfer.hasContent && (
                                                                                <button
                                                                                    type="button"
                                                                                    className="ml-2 font-sans text-primary underline underline-offset-2 hover:opacity-80"
                                                                                    onClick={() => {
                                                                                        void onDownloadTransfer(
                                                                                            transfer.id,
                                                                                        );
                                                                                    }}
                                                                                >
                                                                                    Download
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                        {transfer.errorMessage && (
                                                                            <div className="text-red-700">
                                                                                {
                                                                                    transfer.errorMessage
                                                                                }
                                                                            </div>
                                                                        )}
                                                                    </li>
                                                                ),
                                                            )}
                                                        </ul>
                                                    )}
                                                    {submission.errorMessage && (
                                                        <p className="mt-2 text-xs text-red-700">
                                                            {submission.errorMessage}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </Fragment>
                        );
                    })}

                    {submissions.length === 0 && (
                        <tr>
                            <td
                                colSpan={9}
                                className="px-4 py-8 text-center text-sm text-muted-foreground"
                            >
                                No SFTP submissions yet.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </Card>
    );
};

// ==========================================
// View factory
// ==========================================

/**
 * Creates the Company Settings view component
 */
export const createViewCompanySettings = ({ useCases }: TViewCompanySettingsParams) => {
    const ViewCompanySettings: React.FC<TViewCompanySettingsProps> = () => {
        const {
            onMount,
            onUnmount,
            useThresholdsState,
            useSftpSettingsState,
            useSftpSubmissionsState,
            useMasterDataSettingsState,
            useMasterDataSyncRunsState,
            onRefresh,
            onCreateThreshold,
            onUpdateThreshold,
            onDeleteThreshold,
            onSaveSftpSettings,
            onTestSftpConnection,
            onDownloadTransferPayload,
            onSaveMasterDataSettings,
            onSyncMasterDataNow,
            useSftpEnvDefaults,
            permissions,
            isProdaAdmin,
        } = useCases();

        const perms = permissions();
        const canCreate = perms.includes("settings_threshold_create");
        const canEdit = perms.includes("settings_threshold_edit");
        const canDelete = perms.includes("settings_threshold_delete");
        const showSftpConnection = isProdaAdmin();
        const showMasterDataSync = isProdaAdmin();

        const thresholdsState = useThresholdsState();
        const sftpSettingsState = useSftpSettingsState();
        const sftpEnvDefaults = useSftpEnvDefaults();
        const sftpSubmissionsState = useSftpSubmissionsState();
        const masterDataSettingsState = useMasterDataSettingsState();
        const masterDataSyncRunsState = useMasterDataSyncRunsState();

        // The SFTP Connection and Master Data Sync tabs are internal (Proda
        // admin) only — other admins see no trace of them.
        const tabs: { id: TabId; label: string; icon: typeof Server }[] = [
            ...(showSftpConnection
                ? [{ id: "sftp-connection" as const, label: "SFTP Connection", icon: Server }]
                : []),
            { id: "submission-schedule", label: "Submission Schedule", icon: CalendarClock },
            { id: "sftp-activity", label: "SFTP Activity", icon: Activity },
            ...(showMasterDataSync
                ? [{ id: "master-data-sync" as const, label: "Master Data Sync", icon: RefreshCw }]
                : []),
        ];

        const [activeTab, setActiveTab] = useState<TabId>(tabs[0]?.id ?? "submission-schedule");

        useEffect(() => {
            onMount();
            return onUnmount;
        }, [onMount, onUnmount]);

        const renderActiveTab = () => {
            switch (activeTab) {
                case "sftp-connection":
                    return showSftpConnection ? (
                        <SftpSettingsSection
                            sftpSettingsState={sftpSettingsState}
                            envDefaults={sftpEnvDefaults}
                            onRefresh={onRefresh}
                            onSave={onSaveSftpSettings}
                            onTestConnection={onTestSftpConnection}
                        />
                    ) : null;
                case "submission-schedule":
                    return (
                        <ThresholdsSection
                            thresholdsState={thresholdsState}
                            canCreate={canCreate}
                            canEdit={canEdit}
                            canDelete={canDelete}
                            onRefresh={onRefresh}
                            onCreate={onCreateThreshold}
                            onUpdate={onUpdateThreshold}
                            onDelete={onDeleteThreshold}
                        />
                    );
                case "sftp-activity":
                    return (
                        <SftpActivitySection
                            submissionsState={sftpSubmissionsState}
                            onRefresh={onRefresh}
                            onDownloadTransfer={onDownloadTransferPayload}
                        />
                    );
                case "master-data-sync":
                    return showMasterDataSync ? (
                        <MasterDataSyncSection
                            settingsState={masterDataSettingsState}
                            syncRunsState={masterDataSyncRunsState}
                            onRefresh={onRefresh}
                            onSave={onSaveMasterDataSettings}
                            onSyncNow={onSyncMasterDataNow}
                        />
                    ) : null;
            }
        };

        return (
            <PageLayout
                title="File Submissions"
                description="SFTP submission configuration and activity"
                icon={<Send className="h-6 w-6" />}
            >
                {/* Tabs */}
                <div className="border-b mb-6">
                    <nav className="flex gap-6" aria-label="Tabs">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={cn(
                                    "flex items-center gap-2 py-3 text-sm font-medium border-b-2 -mb-px transition-colors",
                                    activeTab === tab.id
                                        ? "border-primary text-primary"
                                        : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground",
                                )}
                            >
                                <tab.icon className="h-4 w-4" />
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Tab Content */}
                {renderActiveTab()}
            </PageLayout>
        );
    };

    return ViewCompanySettings;
};
